/**
 * LatencySparkline Component
 * Mini line chart showing latency history
 */

import React, { useMemo } from 'react';
import { LatencyHistoryEntry, DependencyKey, HealthStatus } from '../types';
import styles from '../SystemHealth.module.css';

interface LatencySparklineProps {
  history: LatencyHistoryEntry[];
  dependencyKey: DependencyKey;
  status: HealthStatus;
  width?: number;
  height?: number;
}

export const LatencySparkline: React.FC<LatencySparklineProps> = ({
  history,
  dependencyKey,
  status,
  width = 48,
  height = 20
}) => {
  const pathData = useMemo(() => {
    if (history.length < 2) return null;

    const values = history.map(entry => entry[dependencyKey]);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const range = maxValue - minValue || 1;

    // Add padding to prevent line from touching edges
    const padding = 2;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;

    const points = values.map((value, index) => {
      const x = padding + (index / (values.length - 1)) * chartWidth;
      const y = padding + chartHeight - ((value - minValue) / range) * chartHeight;
      return `${x},${y}`;
    });

    return `M ${points.join(' L ')}`;
  }, [history, dependencyKey, width, height]);

  if (!pathData) {
    return (
      <div 
        className={styles.sparklinePlaceholder}
        style={{ width, height }}
      >
        <span className={styles.sparklinePlaceholderText}>—</span>
      </div>
    );
  }

  const strokeColor = {
    healthy: 'var(--status-success, #56b68b)',
    degraded: 'var(--status-warning, #e6a117)',
    error: 'var(--status-error, #da3030)',
    loading: 'var(--status-neutral, #afb0b4)'
  }[status];

  return (
    <svg 
      width={width} 
      height={height} 
      className={styles.sparkline}
      aria-hidden="true"
    >
      <path
        d={pathData}
        fill="none"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export default LatencySparkline;
